import { useEffect, useState, Fragment } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import QuickHitsEditor from "@/components/QuickHitsEditor";
import { RolesPanel } from "@/components/roles/RolesPanel";
import { SalesSwitch } from "@/components/sales/SalesSwitch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function PermissionsPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [pending, setPending] = useState(null);   // {role, cap, enabled, label}

  const load = async () => {
    try { const { data } = await api.get("/admin/permissions"); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (role, cap, enabled) => {
    setBusy(`${role}:${cap}`);
    try {
      await api.put("/admin/permissions", { role, capability: cap, enabled });
      setData((d) => ({ ...d, grid: { ...d.grid, [role]: { ...d.grid[role], [cap]: enabled } } }));
      toast.success(`${nameOf(role)} · ${cap} ${enabled ? "enabled" : "disabled"} — effective immediately`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const confirmToggle = async () => {
    if (!pending) return;
    const { role, cap, enabled } = pending;
    setPending(null);
    await toggle(role, cap, enabled);
  };

  const nameOf = (r) => data?.names?.[r] || r;
  if (!data) return <div className="font-mono text-xs text-slate-500">LOADING…</div>;

  const groups = [...new Set(data.capabilities.map((c) => c.group))];

  return (
    <div data-testid="permissions-page">
      <h1 className="font-display text-2xl font-bold tracking-tight">Permission Grid</h1>
      <p className="text-sm text-slate-500 mb-5">Admin toggles capability per role · server-enforced · every change is audited</p>
      <SalesSwitch />
      <RolesPanel onChanged={load} />

      <div className="border border-slate-200 bg-white overflow-auto max-h-[70dvh]">
        <table className="w-full text-sm border-separate border-spacing-0">
          <thead>
            <tr className="bg-slate-100 font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <th className="py-2 px-3 border-r border-b border-slate-200 text-left sticky left-0 top-0 z-30 bg-slate-100">Capability</th>
              {data.roles.map((r) => (
                <th key={r} className="py-2 px-3 border-r border-b border-slate-200 text-center whitespace-nowrap sticky top-0 z-20 bg-slate-100" data-testid={`perm-col-${r.replace(/\s/g, "-")}`}>{nameOf(r)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={`g-${g}`}>
                <tr className="bg-slate-50">
                  <td colSpan={data.roles.length + 1} className="py-1.5 px-3 font-mono text-[10px] uppercase tracking-widest text-[#B01E28] font-bold sticky left-0 bg-slate-50">{g}</td>
                </tr>
                {data.capabilities.filter((c) => c.group === g).map((c) => (
                  <tr key={c.key} data-testid={`perm-row-${c.key}`}>
                    <td className="py-2 px-3 border-r border-t border-slate-100 sticky left-0 z-10 bg-white max-w-[320px]">{c.label}
                      <span className="block font-mono text-[10px] text-slate-400">{c.key}</span>
                      {c.desc && <span className="block text-[11px] text-slate-500 mt-0.5 font-normal normal-case">{c.desc}</span>}
                      {c.warn && <span data-testid={`perm-warn-${c.key}`} className="block text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mt-1 font-normal normal-case">⚠ {c.warn}</span>}
                    </td>
                    {data.roles.map((r) => {
                      const on = !!data.grid[r]?.[c.key];
                      const locked = r === "Admin";
                      return (
                        <td key={r} className="py-2 px-3 border-r border-t border-slate-100 text-center">
                          {locked
                            ? <Badge className="rounded-lg bg-slate-900 text-white text-[9px]">ALL</Badge>
                            : <Switch
                                data-testid={`toggle-${r.replace(/\s/g, "-")}-${c.key}`}
                                checked={on}
                                disabled={busy === `${r}:${c.key}`}
                                onCheckedChange={(v) => setPending({ role: r, cap: c.key, enabled: v, label: c.label })}
                              />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-3 font-mono">Admin is the superuser — always full access, cannot be toggled.</p>

      <QuickHitsEditor />

      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent data-testid="perm-confirm-dialog" className="rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.enabled ? "Give" : "Remove"} this permission?</AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.enabled
                ? <>Give <b>{nameOf(pending?.role)}</b> the ability to <b>{pending?.label}</b>? This takes effect immediately for everyone in that role.</>
                : <>Remove <b>{nameOf(pending?.role)}</b>'s ability to <b>{pending?.label}</b>? This takes effect immediately for everyone in that role.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="perm-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="perm-confirm-apply" onClick={confirmToggle} className={pending?.enabled ? "bg-[#E0781A] hover:bg-[#C76714]" : "bg-red-600 hover:bg-red-700"}>
              {pending?.enabled ? "Grant access" : "Remove access"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
